import { toast } from '@postscript/components';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { toastError } from 'components/billing/common/utils';
import { QueryCacheTimes } from 'components/flowBuilder/constants';
import { useUser } from 'controllers/contexts/user';
import { api } from 'controllers/network/apiClient';
import { SHOPPER_STATUS_QUERY_KEY } from 'providers/shopperStatusProvider';
import {
  brandingKey,
  brandingStatusKey,
  initAIGeneratedBrandingKey,
  inpirationMessagesKey,
  productCatalogMapGenerationStatusKey,
} from '../constants';
import {
  Branding,
  BrandingFileDownloadUrl,
  BrandingStatus,
  BrandingType,
  BrandingValues,
  BrandVoice,
  GeneratedMessage,
  InspirationPayload,
  PersistRangeBoundaryParams,
  ProductCatalogMapGenerationRequest,
  ProductCatalogMapGenerationResponse,
  RangeAttributeBoundaries,
} from '../types';
import { buildBrandingPayload, toCamelCase, toSnakeCase } from '../utils';

export const BRAND_VOICE_QUERY_KEY = 'brand-voice';

const isRunningProductCatalogMapGeneration = (
  status?: ProductCatalogMapGenerationResponse['status'],
) => status === 'started' || status === 'in_progress';

const rangeBoundaries: RangeAttributeBoundaries = new Map();

export function usePersistRangeBoundary({
  name,
  min,
  max,
}: PersistRangeBoundaryParams): void {
  rangeBoundaries.set(name, [min, max]);
}

export const useGetBrandVoice = (): UseQueryResult<BrandVoice> => {
  return useQuery({
    queryKey: [BRAND_VOICE_QUERY_KEY],
    queryFn: async () => {
      return api.get('/v2/ai-components/brand-voice');
    },
  });
};

export const useGetBrandingStatus = (
  options?: UseQueryOptions<
    BrandingStatus,
    any,
    BrandingStatus,
    [typeof brandingStatusKey]
  >,
): UseQueryResult<BrandingStatus> => {
  return useQuery({
    queryKey: [brandingStatusKey],
    queryFn: async (): Promise<BrandingStatus> => {
      const brandingStatus = await api.get(
        '/v2/marketing-ai/brand-voices/status',
        {
          throwErrorAsString: false,
        },
      );
      return brandingStatus;
    },
    staleTime: QueryCacheTimes.SHORT,
    ...options,
  });
};

export function useInitAIGeneratedBranding(): UseMutationResult<
  void,
  any,
  void
> {
  const queryClient = useQueryClient();
  const {
    user: { shop_id: shopId },
  } = useUser();

  return useMutation({
    mutationFn: async () => {
      queryClient.setQueryData(
        [brandingKey, 'ai_generated'],
        toCamelCase(
          await api.post('/v2/branding/ai_generated', {
            shop_id: shopId,
          }),
        ),
      );
    },
    mutationKey: [initAIGeneratedBrandingKey],
  });
}

export function useUpdateBranding(): UseMutationResult<
  Branding,
  any,
  { guid: string; values: BrandingValues }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ guid, values }) => {
      const payload = buildBrandingPayload(values, rangeBoundaries);
      return api.post(`/v2/branding/${guid}/attributes`, toSnakeCase(payload));
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [brandingKey, data.type.toLowerCase()],
        toCamelCase(data),
      );
      queryClient.invalidateQueries([SHOPPER_STATUS_QUERY_KEY]);
    },
    onError: toastError,
  });
}

export function useStartProductCatalogMapGeneration(): UseMutationResult<
  ProductCatalogMapGenerationResponse,
  any,
  ProductCatalogMapGenerationRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      return toCamelCase(
        await api.post(
          '/v2/branding/product-catalog-map/generation',
          toSnakeCase(payload),
          { throwErrorAsString: false },
        ),
      );
    },
    onSuccess: (data) => {
      queryClient.setQueryData([productCatalogMapGenerationStatusKey], data);
    },
  });
}

export function useProductCatalogMapGenerationStatus(): UseQueryResult<ProductCatalogMapGenerationResponse> {
  return useQuery({
    queryKey: [productCatalogMapGenerationStatusKey],
    queryFn: async (): Promise<ProductCatalogMapGenerationResponse> => {
      return toCamelCase(
        await api.get('/v2/branding/product-catalog-map/generation/status', {
          throwErrorAsString: false,
        }),
      );
    },
    refetchInterval: (data) =>
      isRunningProductCatalogMapGeneration(data?.status) ? 3000 : false,
  });
}

export const useInspirationMessages = (): UseMutationResult<
  GeneratedMessage[],
  any,
  { payload: InspirationPayload }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ payload }) => {
      const { generated_messages: generatedMessages } = await api.post(
        '/v2/marketing-ai/brand-voices/preview-messages-mai',
        toSnakeCase(payload),
      );
      return toCamelCase(generatedMessages);
    },
    onSuccess: (data) => {
      queryClient.setQueryData([inpirationMessagesKey], data);
    },
    onError: () =>
      toast.error('Error retrieving example messages. Try again later.'),
  });
};

export function useUploadFile(
  options = {
    maxImageSize: 50 * 1024 * 1024, // 50 MB
    maxImageSizeError: 'Must be smaller than 50 MB',
  },
): UseMutationResult<string, any, File> {
  return useMutation({
    mutationFn: async (file) => {
      const { maxImageSize, maxImageSizeError } = options;

      if (file.size > maxImageSize) {
        throw new Error(maxImageSizeError);
      }

      const formData = new FormData();
      formData.append('file', file);

      const { url } = await api.postFormData('/v2/branding/file', formData);
      return url;
    },
  });
}

export function useCreateBrandingByType(): UseMutationResult<
  Branding,
  any,
  BrandingType
> {
  const queryClient = useQueryClient();
  const {
    user: { shop_id: shopId },
  } = useUser();

  return useMutation({
    mutationFn: async (type) => {
      return api.post('/v2/branding/', {
        shop_id: shopId,
        type,
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [brandingKey, data.type.toLowerCase()],
        toCamelCase(data),
      );
    },
    onError: () => toast.error('Something went wrong. Try again later.'),
  });
}

export function useGetBrandingByType(
  type: BrandingType,
  options?: UseQueryOptions<
    Branding,
    any,
    Branding,
    [typeof brandingKey, BrandingType]
  >,
): UseQueryResult<Branding> {
  return useQuery({
    queryKey: [brandingKey, type],
    queryFn: async (): Promise<Branding> => {
      // `throwErrorAsString: false` preserves the AxiosError so consumers can
      // branch on `error.response.status` (e.g. the 404 recovery path in
      // BrandingForm / AIGeneratedValuesInjector). Without this, the error
      // gets coerced to the backend's message string, which only contains
      // "404" when the response body is empty.
      return toCamelCase(
        await api.get(`/v2/branding/${type}`, { throwErrorAsString: false }),
      );
    },
    staleTime: QueryCacheTimes.SHORT,
    ...options,
  });
}

export function useGenerateFileAssistedAttributes(): UseMutationResult<
  Branding,
  any,
  { guid: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ guid }) => {
      return api.post(`/v2/branding/${guid}/attributes/file_assisted`);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [brandingKey, data.type.toLowerCase()],
        toCamelCase(data),
      );
    },
    onError: toastError,
  });
}

/** Imperatively fetch a download URL on demand (e.g., download button click). */
export function useFetchFileDownloadUrl(): UseMutationResult<
  BrandingFileDownloadUrl,
  any,
  number
> {
  return useMutation({
    mutationFn: async (fileId: number): Promise<BrandingFileDownloadUrl> => {
      return api.get(`/v2/branding/sources/files/${fileId}/download`);
    },
  });
}
